/**
 * Drag-to-resize table columns, with the widths remembered per table.
 *
 * Extracted from ConsBusinessTable, which had the only working implementation in the app.
 * Two details in here are the ones a re-implementation always misses:
 *
 *   1. `shouldSuppressClick()` — a drag ends with a mouseup inside the header cell, which the
 *      browser then reports as a click. Without swallowing exactly that one click, every
 *      resize also toggles the column's sort.
 *   2. The listeners live on `window`, not on the cell, so the pointer may leave the header
 *      mid-drag. They are torn down by the effect, so a component unmounted mid-drag leaves
 *      nothing behind.
 *
 * localStorage, NOT sessionStorage: a width the user dragged has to outlive the tab, the
 * browser restart and tomorrow morning. It is scoped to the browser profile, so it is per
 * user in practice on a personal machine — but it does not follow the user to another
 * device, which would need a server-side preference store this app does not have yet.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const MIN_COL_WIDTH = 60;
export const MAX_COL_WIDTH = 900;

/** The shape a column has to have to be resizable. Callers usually have a richer ColDef. */
export interface ResizableColumn {
    key: string;
    /** Default width in px, used until the user drags this column. */
    width: number;
}

interface Options {
    min?: number;
    max?: number;
}

/**
 * Widths the user dragged, by column key. Anything unparseable, unknown or out of range is
 * dropped rather than clamped into place — a stored width is a convenience, and a column that
 * has silently become 3px wide is worse than one at its default.
 */
function loadWidths(storageKey: string, knownKeys: Set<string>, min: number, max: number): Record<string, number> {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const out: Record<string, number> = {};
        for (const [k, v] of Object.entries(parsed)) {
            const n = Number(v);
            if (knownKeys.has(k) && Number.isFinite(n) && n >= min && n <= max) out[k] = n;
        }
        return out;
    } catch {
        return {};
    }
}

/**
 * @param columns   ALL columns of the table, including ones currently hidden. Validation drops
 *                  unknown keys, so passing only the visible subset would delete the widths of
 *                  hidden columns the next time they are persisted.
 * @param storageKey unique per table, e.g. `bab-ytd-col-widths`.
 */
export function useResizableColumns(columns: ResizableColumn[], storageKey: string, opts: Options = {}) {
    const min = opts.min ?? MIN_COL_WIDTH;
    const max = opts.max ?? MAX_COL_WIDTH;

    const defaults = useMemo(() => {
        const m: Record<string, number> = {};
        for (const c of columns) m[c.key] = c.width;
        return m;
    }, [columns]);

    const [widths, setWidths] = useState<Record<string, number>>(
        () => loadWidths(storageKey, new Set(columns.map(c => c.key)), min, max)
    );
    const [resizing, setResizing] = useState<{ key: string; startX: number; startWidth: number } | null>(null);
    const suppressClick = useRef(false);

    const widthOf = useCallback(
        (key: string) => widths[key] ?? defaults[key] ?? min,
        [widths, defaults, min]
    );

    /** Sum over the columns actually rendered — the caller decides which those are. */
    const totalWidthOf = useCallback(
        (keys: string[]) => keys.reduce((sum, k) => sum + widthOf(k), 0),
        [widthOf]
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try { window.localStorage.setItem(storageKey, JSON.stringify(widths)); } catch { /* quota */ }
    }, [widths, storageKey]);

    useEffect(() => {
        if (!resizing) return;
        const onMove = (ev: globalThis.MouseEvent) => {
            const next = Math.min(max, Math.max(min, resizing.startWidth + ev.clientX - resizing.startX));
            setWidths(w => ({ ...w, [resizing.key]: next }));
        };
        const onUp = () => {
            suppressClick.current = true;
            // Clears itself in case the mouse came up outside the cell and no click ever arrives.
            window.setTimeout(() => { suppressClick.current = false; }, 200);
            setResizing(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        const prev = { cursor: document.body.style.cursor, select: document.body.style.userSelect };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.style.cursor = prev.cursor;
            document.body.style.userSelect = prev.select;
        };
    }, [resizing, min, max]);

    const startResize = useCallback((key: string, clientX: number) => {
        setResizing({ key, startX: clientX, startWidth: widthOf(key) });
    }, [widthOf]);

    /** Back to the column's default. */
    const resetWidth = useCallback((key: string) => {
        setWidths(w => {
            const next = { ...w };
            delete next[key];
            return next;
        });
    }, []);

    /**
     * Call at the top of the header's onClick. `true` means "this click is the tail of a
     * drag, ignore it".
     */
    const shouldSuppressClick = useCallback(() => {
        if (!suppressClick.current) return false;
        suppressClick.current = false;
        return true;
    }, []);

    return { widthOf, totalWidthOf, resizingKey: resizing?.key ?? null, startResize, resetWidth, shouldSuppressClick };
}
